import { createContext, useContext, useState, useEffect } from 'react';
import { getCurrentUser, fetchAuthSession, signOut as amplifySignOut } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { useNavigate } from 'react-router-dom';

const AuthContext = createContext(null);

/**
 * Provides authentication state across the app.
 * Wraps AWS Amplify auth; exposes user info, login state, and auth actions.
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadUser();

    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'signedIn':
        case 'autoSignIn':
          loadUser();
          break;
        case 'signedOut':
          setUser(null);
          break;
        case 'tokenRefresh':
          loadUser();
          break;
        default:
          break;
      }
    });

    return () => unsubscribe();
  }, []);

  async function loadUser(forceRefresh = false) {
    try {
      const cognitoUser = await getCurrentUser();
      const session = await fetchAuthSession({ forceRefresh });
      const claims = session.tokens?.idToken?.payload || {};
      setUser({
        userId: cognitoUser.userId,
        email: claims.email || cognitoUser.signInDetails?.loginId || '',
        tier: claims['custom:subscription_tier'] || 'free',
        displayName: claims['custom:display_name'] || ''
      });
    } catch {
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }

  async function signOut() {
    await amplifySignOut();
    setUser(null);
  }

  const value = {
    user,
    isLoggedIn: user !== null,
    isPro: user?.tier === 'pro',
    isLoading,
    signOut,
    refreshUser: loadUser
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Hook to access auth state. Must be used inside <AuthProvider>. */
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

/**
 * Redirects to /login with a returnTo param if the user is not logged in.
 * Use inside components that require authentication.
 */
export function useRequireAuth(returnTo) {
  const { isLoggedIn, isLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && !isLoggedIn) {
      const path = returnTo || window.location.pathname;
      navigate(`/login?returnTo=${encodeURIComponent(path)}`);
    }
  }, [isLoggedIn, isLoading, navigate, returnTo]);

  return { isLoggedIn, isLoading };
}

export default AuthContext;
